/**
 * The purchase-order number and discount-code box, with the "flexible shipping dates" checkbox,
 * shown on the cart page (`ShoppingCart/ShoppingCartDesktop.tsx`, `ShoppingCartMobile.tsx`),
 * order review (`Checkout/OrderReview/OrderReviewDesktop.tsx`, `OrderReviewMobile.tsx`) and
 * express checkout for a cart or a quote (`Checkout/ExpressCheckout/ExpressCheckoutDesktop.tsx`,
 * `ExpressCheckoutMobile.tsx`). This file holds the logic; the Desktop / Mobile files render it.
 *
 * Where the values live. The PO number, the quote PO number, the discount code and the
 * flexible-delivery flag belong to `@ryerson/frontend.e-commerce` → `useEcommerce()`, so they
 * survive from cart to review to order placement. This component edits local copies and writes
 * them back.
 *
 * PO number. Capped at 15 characters (`MAX_FIELD_LENGTHS.HEADER_PO`) and checked for characters
 * the ERP rejects (`validateErpRestrictedCharacters`) by the view. 500 ms after typing stops it
 * is written to the e-commerce state and, for a signed-in cart, saved on the cart
 * (`Helper/shoppingCartHelper.ts` → `updateUsersShoppingCart`, `PUT shopping-cart`). Quote
 * checkout keeps its own `quotePurchaseOrderNumber`.
 *
 * Discount code. Shown only when the session's sales plant (`sessionStorage` `salesPlant`) has
 * `isCouponAllowed`, and only for cart or quote checkout.
 * - Cart: Apply puts the typed code into the e-commerce `discountCode`. The page's context
 *   reprices with it as `couponCode` (for the cart page,
 *   `ShoppingCart/Context/ShoppingCartContext.tsx` → `setPricingForCartData`) and passes the
 *   response back as `previousPricingData`. Its `discount.status` (`DiscountCodeResponseType`)
 *   decides the outcome here. The Prices API sets `VALID` when a coupon was sent and a discount
 *   came back, `INVALID` when a coupon was sent and none came back, and `NONE` when no coupon
 *   was sent (`web-prices-api/application/src/enums/pricing.enum.ts` → `EDiscountStatus`).
 *   `VALID` and `NONE` take the same branch. For a signed-in cart a `VALID` code is saved on
 *   the cart and "Discount Applied" shows once the save returns; for a guest cart the message
 *   shows straight away, for `NONE` as well. The message lasts 5 s. `INVALID` shows "Invalid
 *   discount code", and if it replaced a valid code, the valid code is removed from the
 *   signed-in cart.
 * - Quote: Apply calls `applyDiscountCodeForQuoteCheckout`
 *   (`Checkout/ExpressCheckout/Context/ExpressCheckoutContext.tsx`, `POST` quote discount),
 *   which updates `previousPricingData` in the same way.
 *
 * Flexible shipping dates are a discount code: ticking the box sets `discountCode` to
 * `FLEXSHIP` (`@ryerson/frontend.common`) and clears any typed code; unticking clears it. Cart
 * only, and only when the caller passes `showFlexDates`. In exchange for later delivery dates
 * the customer gets a lower price or freight saving, and Ryerson gets consolidated shipping.
 */
import React, { useEffect, useState } from "react";
import { Media } from "@ryerson/frontend.next";
import PurchaseOrderDiscountCodeMobile from "./PurchaseOrderDiscountCodeMobile";
import PurchaseOrderDiscountCodeDesktop from "./PurchaseOrderDiscountCodeDesktop";
import {
	ShoppingCartTableContent,
	ShoppingCartPurchaseOrderText,
} from "./PurchaseOrderDiscountCodeContent";
import { useApplication } from "@ryerson/frontend.application";
import {
	getSessionItem,
	SessionItems,
	ShoppingCartPricingResponse,
	useDebounce,
} from "@ryerson/frontend.common";
import {
	DiscountCodeResponseType,
	updateUsersShoppingCart,
} from "@components/Helper/shoppingCartHelper";
import { CheckOutType, useEcommerce } from "@ryerson/frontend.e-commerce";
import { FLEXSHIP } from "@ryerson/frontend.common";
import { useFeatureToggles } from "@ryerson/frontend.feature-toggles";
import { replaceContentWithCompanyName } from "@components/Checkout/Shared/Utility.functions";

/**
 * Props of the Desktop / Mobile views. State and handlers come from
 * {@link PurchaseOrderDiscountCode}.
 */
export type PurchaseOrderProps = {
	testingId?: string;
	/** Whether flexible delivery (the `FLEXSHIP` code) is on. */
	flexDates: boolean;
	showFlexDates?: boolean;
	/** `true` toggles flexible shipping dates; `false` applies the typed discount code. */
	updateDiscountCode: (value: boolean) => void;
	currentDiscountCode: string;
	setCurrentDiscountCode: (value: string) => void;
	cartData: any;
	content: ShoppingCartPurchaseOrderText;
	showDiscountMessage: boolean;
	discountMessage: string;
	currentPurchaseOrderNumber: string;
	setCurrentPurchaseOrderNumber: (value: string) => void;
	validationTriggered: boolean;
	purchaseOrderRequired?: boolean;
	isSubmitted?: boolean;
	POInputRef?: React.RefObject<HTMLInputElement>;
	/** The sales plant's `isCouponAllowed`; false hides the discount-code field. */
	isCouponAllowed?: boolean;
};

/** Props of {@link PurchaseOrderDiscountCode}. */
export type PurchaseOrderType = {
	/** Id of the flexible-dates checkbox. */
	testingId?: string;
	/** Shows the flexible-shipping-dates checkbox; ignored in quote checkout. */
	showFlexDates?: boolean;
	/** The lines being bought; both inputs are disabled when it is empty. */
	cartData: any;
	/**
	 * Latest pricing (or quote discount) response; its `discount` drives the discount-code outcome.
	 */
	previousPricingData: ShoppingCartPricingResponse;
	/** Marks the PO input invalid when it is empty or has ERP-restricted characters. */
	validationTriggered?: boolean;
	/**
	 * Shows the required asterisk in both views; on desktop it also sets the PO input's
	 * `required`, which the mobile view always sets.
	 */
	purchaseOrderRequired?: boolean;
	isSubmitted?: boolean;
	/** Attached to the PO row so the cart can scroll to it when the PO is invalid. */
	POInputRef?: React.RefObject<HTMLInputElement>;
	/** Quote checkout only: validates and applies a code against the quote. */
	applyDiscountCodeForQuoteCheckout?: (value: string) => void;
	hasQuoteDiscountApplied?: boolean; // This is used to check if a discount code was applied on the quote checkout
};

/**
 * PO number and discount-code logic; see the file header. Side effects: `PUT shopping-cart` for
 * a signed-in cart when the PO number or a valid discount code changes, and writes to the
 * `useEcommerce()` state.
 */
const PurchaseOrderDiscountCode: React.FC<PurchaseOrderType> = ({
	testingId,
	showFlexDates = false,
	cartData,
	previousPricingData,
	validationTriggered = false,
	purchaseOrderRequired = false,
	isSubmitted = false,
	POInputRef,
	applyDiscountCodeForQuoteCheckout,
	hasQuoteDiscountApplied = false, // This is used to check if a discount code was applied on the quote checkout
}) => {
	const {
		localization: { language },
		axiosInstance,
		user: { isLoggedIn },
	} = useApplication();

	const {
		discountCode,
		setDiscountCode,
		purchaseOrderNumber,
		setPurchaseOrderNumber,
		quotePurchaseOrderNumber,
		setQuotePurchaseOrderNumber,
		flexibleDelivery,
		setFlexibleDelivery,
		checkoutType,
		shoppingCart,
		setShoppingCart,
		quoteCartPricingData,
	} = useEcommerce();

	const { isFeatureEnabled } = useFeatureToggles();

	const [hasMounted, setHasMounted] = useState<boolean>(false);

	const [currentPurchaseOrderNumber, setCurrentPurchaseOrderNumber] =
		useState<string>(purchaseOrderNumber);
	const [currentQuotePurchaseOrderNumber, setCurrentQuotePurchaseOrderNumber] =
		useState<string>(quotePurchaseOrderNumber);
	// `undefined` until the shopper applies a code or toggles flexible dates here (and again
	// after sign-in or sign-out). While undefined, a pricing response in cart checkout shows no
	// message and makes no cart call, so a response that arrives on page load is not treated as
	// an "apply".
	const [previousDiscountCode, setPreviousDiscountCode] = useState<string | undefined>(undefined);
	const [showDiscountMessage, setShowDiscountMessage] = useState<boolean>(false);
	const [discountMessage, setDiscountMessage] = useState<string>("");

	const currentPurchaseOrderNumberDebounced = useDebounce(currentPurchaseOrderNumber, 500);
	const currentQuotePurchaseOrderNumberDebounced = useDebounce(
		currentQuotePurchaseOrderNumber,
		500
	);

	const [currentDiscountCode, setCurrentDiscountCode] = useState<string>("");
	const [hasValidDiscountCodeApplied, setHasValidDiscountCodeApplied] = useState<boolean>(false);

	// Note: Replaces content with company name dynamically
	const content = React.useMemo(() => {
		return replaceContentWithCompanyName<ShoppingCartPurchaseOrderText>(
			language,
			ShoppingCartTableContent[language] || ShoppingCartTableContent.en
		);
	}, [hasMounted, language]);

	useEffect(() => {
		setHasMounted(true);
	}, []);

	useEffect(() => {
		setPreviousDiscountCode(undefined);
	}, [isLoggedIn]);

	useEffect(() => {
		if (checkoutType === CheckOutType.CART) {
			setFlexibleDelivery(discountCode === FLEXSHIP ? true : false);
		}
	}, [discountCode]);

	useEffect(() => {
		setCurrentPurchaseOrderNumber(purchaseOrderNumber);
	}, [purchaseOrderNumber]);

	useEffect(() => {
		setCurrentQuotePurchaseOrderNumber(quotePurchaseOrderNumber);
	}, [quotePurchaseOrderNumber]);

	useEffect(() => {
		setShowDiscountMessage(false);
		setDiscountMessage("");
	}, [currentDiscountCode, isLoggedIn]);

	useEffect(() => {
		updateCartPO();
	}, [currentPurchaseOrderNumberDebounced]);

	useEffect(() => {
		updateQuotePO();
	}, [currentQuotePurchaseOrderNumberDebounced]);

	// A cart that arrives already carrying a valid code is marked, so that a later invalid code
	// can remove it (see `handleInvalidDiscountCode`).
	useEffect(() => {
		if (
			!hasValidDiscountCodeApplied &&
			previousDiscountCode === undefined &&
			previousPricingData?.discount?.status === DiscountCodeResponseType.VALID
		) {
			setHasValidDiscountCodeApplied(true);
		}
	}, [previousPricingData]);

	// Reacts to each new pricing / quote-discount response; the file header lists the outcomes.
	// The final else (any other status, or an INVALID this component did not ask for) clears the
	// shared discount code.
	useEffect(() => {
		if (
			previousPricingData.discount?.status === DiscountCodeResponseType.VALID ||
			previousPricingData.discount?.status === DiscountCodeResponseType.NOTAPPLIED
		) {
			if (discountCode === FLEXSHIP) {
				if (previousDiscountCode !== undefined) {
					if (isLoggedIn && checkoutType === CheckOutType.CART) {
						updateUsersShoppingCart(
							axiosInstance,
							currentPurchaseOrderNumber,
							discountCode,
							handleOnSuccessDiscountCode
						);
					} else {
						setDiscountCode(discountCode);
						handleOnSuccessDiscountCode();
					}
				}
			} else {
				if (
					currentDiscountCode !== previousDiscountCode &&
					previousDiscountCode !== undefined
				) {
					if (isLoggedIn && checkoutType === CheckOutType.CART) {
						setPreviousDiscountCode(currentDiscountCode);
						if (discountCode !== currentDiscountCode) {
							setDiscountCode(currentDiscountCode);
						}
						previousPricingData.discount?.status === DiscountCodeResponseType.VALID &&
							updateUsersShoppingCart(
								axiosInstance,
								currentPurchaseOrderNumber,
								discountCode,
								handleOnSuccessDiscountCode
							);
					} else {
						setDiscountCode(discountCode);
						handleOnSuccessDiscountCode();
					}
				} else {
					if (checkoutType === CheckOutType.QUOTE && hasQuoteDiscountApplied) {
						handleOnSuccessDiscountCode();
					}
				}
			}
		} else if (
			previousPricingData.discount?.status === DiscountCodeResponseType.INVALID &&
			previousPricingData.totalPrice &&
			previousDiscountCode !== undefined &&
			(discountCode !== "" ||
				(checkoutType === CheckOutType.QUOTE &&
					hasQuoteDiscountApplied &&
					currentDiscountCode !== ""))
		) {
			setDiscountMessage(content.invalidDiscount);
			setShowDiscountMessage(true);
			handleInvalidDiscountCode();
		} else {
			showDiscountMessage && setShowDiscountMessage(false);
			discountMessage && setDiscountMessage("");
			setDiscountCode("");
		}
	}, [previousPricingData]);

	useEffect(() => {
		if (flexibleDelivery) {
			if (!isLoggedIn) {
				setFlexibleDelivery(true);
			}
			setDiscountCode(FLEXSHIP);
		} else {
			if (!isLoggedIn) {
				setFlexibleDelivery(false);
			}
		}
	}, [flexibleDelivery]);

	/**
	 * Apply button (`isFlex` false) or flexible-dates checkbox (`isFlex` true). In a cart it only
	 * changes the shared `discountCode`; the page's repricing then produces the response the
	 * effect above reacts to. In a quote it calls `applyDiscountCodeForQuoteCheckout` unless the
	 * code is already the quote's.
	 */
	const updateDiscountCode = (isFlex: boolean = false) => {
		if (CheckOutType.CART === checkoutType) {
			if (isFlex) {
				setCurrentDiscountCode("");
				setFlexibleDelivery(!flexibleDelivery);
				discountCode === FLEXSHIP
					? setPreviousDiscountCode(FLEXSHIP)
					: setPreviousDiscountCode("");
				discountCode === FLEXSHIP ? setDiscountCode("") : setDiscountCode(FLEXSHIP);
			} else {
				if (currentDiscountCode !== discountCode) {
					setPreviousDiscountCode(discountCode);
					setDiscountCode(currentDiscountCode);
				}
			}
		} else {
			if (currentDiscountCode !== quoteCartPricingData?.discount?.code) {
				setPreviousDiscountCode(currentDiscountCode);
				applyDiscountCodeForQuoteCheckout &&
					applyDiscountCodeForQuoteCheckout(currentDiscountCode);
			}
		}
	};

	/**
	 * Runs when the debounced cart PO settles: writes it to the shared state and, for a signed-in
	 * cart, saves it with the current discount code on the cart.
	 */
	const updateCartPO = () => {
		if (
			currentPurchaseOrderNumber === currentPurchaseOrderNumberDebounced &&
			currentPurchaseOrderNumber !== purchaseOrderNumber
		) {
			if (isLoggedIn && checkoutType === CheckOutType.CART) {
				setPurchaseOrderNumber(currentPurchaseOrderNumber || "");
				setCurrentPurchaseOrderNumber(currentPurchaseOrderNumber || "");
				updateUsersShoppingCart(axiosInstance, currentPurchaseOrderNumber, discountCode);
			} else {
				setPurchaseOrderNumber(currentPurchaseOrderNumber);
			}
		}
	};

	/**
	 * Runs when the debounced quote PO settles: writes it to the shared `quotePurchaseOrderNumber`
	 * (no API call). When signed out or not in quote checkout, it writes the cart PO
	 * (`currentPurchaseOrderNumber`) there instead.
	 */
	const updateQuotePO = () => {
		if (
			currentQuotePurchaseOrderNumber === currentQuotePurchaseOrderNumberDebounced &&
			currentQuotePurchaseOrderNumber !== quotePurchaseOrderNumber
		) {
			if (isLoggedIn && checkoutType === CheckOutType.QUOTE) {
				setCurrentQuotePurchaseOrderNumber(currentQuotePurchaseOrderNumber);
				setQuotePurchaseOrderNumber(currentQuotePurchaseOrderNumber);
			} else {
				setQuotePurchaseOrderNumber(currentPurchaseOrderNumber);
			}
		}
	};

	/**
	 * After a code is accepted: when the input is not empty, shows "Discount Applied" for 5 s and
	 * then clears the input (for a signed-in cart only when the `PUT shopping-cart` returned
	 * data). In cart checkout it also records the code on the shared `shoppingCart`.
	 */
	const handleOnSuccessDiscountCode = (response?: any) => {
		let responseData = response?.data;
		if (
			(!isLoggedIn || responseData || checkoutType === CheckOutType.QUOTE) &&
			currentDiscountCode !== ""
		) {
			setDiscountMessage(content.discountApplied);
			setShowDiscountMessage(true);
			setTimeout(() => {
				setShowDiscountMessage(false);
				setDiscountMessage("");
				setCurrentDiscountCode(""); // remove discount code from input field after success
			}, 5000);
		}
		if (checkoutType === CheckOutType.CART) {
			setShoppingCart({ ...shoppingCart, discountCode: discountCode });
			setHasValidDiscountCodeApplied(true);
		}
	};

	const handleInvalidDiscountCode = () => {
		const { discount } = previousPricingData || {};
		if (
			checkoutType === CheckOutType.CART &&
			previousDiscountCode &&
			hasValidDiscountCodeApplied &&
			discountCode === discount?.code &&
			discount?.status === DiscountCodeResponseType.INVALID
		) {
			// remove the existing valid discount code when an invalid discount code applied
			if (isLoggedIn) {
				updateUsersShoppingCart(axiosInstance, currentPurchaseOrderNumber, "");
			}
			setHasValidDiscountCodeApplied(false);
		}
	};

	// Read from sessionStorage on every render; the discount-code field shows only for plants
	// that allow coupons.
	const salesPlant = getSessionItem(SessionItems.salesPlant);
	const { isCouponAllowed } = (salesPlant && JSON.parse(salesPlant)) || {};

	return (
		<>
			<Media lessThan="lg">
				<PurchaseOrderDiscountCodeMobile
					POInputRef={POInputRef}
					testingId={testingId}
					flexDates={flexibleDelivery}
					showFlexDates={showFlexDates}
					updateDiscountCode={updateDiscountCode}
					currentDiscountCode={currentDiscountCode}
					setCurrentDiscountCode={setCurrentDiscountCode}
					cartData={cartData}
					content={content}
					showDiscountMessage={showDiscountMessage}
					discountMessage={discountMessage}
					currentPurchaseOrderNumber={
						checkoutType === CheckOutType.CART
							? currentPurchaseOrderNumber
							: currentQuotePurchaseOrderNumber
					}
					setCurrentPurchaseOrderNumber={
						checkoutType === CheckOutType.CART
							? setCurrentPurchaseOrderNumber
							: setCurrentQuotePurchaseOrderNumber
					}
					validationTriggered={validationTriggered}
					purchaseOrderRequired={purchaseOrderRequired}
					isSubmitted={isSubmitted}
					isCouponAllowed={isCouponAllowed}
				/>
			</Media>
			<Media greaterThanOrEqual="lg">
				<PurchaseOrderDiscountCodeDesktop
					testingId={testingId}
					flexDates={flexibleDelivery}
					showFlexDates={showFlexDates}
					updateDiscountCode={updateDiscountCode}
					currentDiscountCode={currentDiscountCode}
					setCurrentDiscountCode={setCurrentDiscountCode}
					cartData={cartData}
					content={content}
					showDiscountMessage={showDiscountMessage}
					discountMessage={discountMessage}
					currentPurchaseOrderNumber={
						checkoutType === CheckOutType.CART
							? currentPurchaseOrderNumber
							: currentQuotePurchaseOrderNumber
					}
					setCurrentPurchaseOrderNumber={
						checkoutType === CheckOutType.CART
							? setCurrentPurchaseOrderNumber
							: setCurrentQuotePurchaseOrderNumber
					}
					validationTriggered={validationTriggered}
					purchaseOrderRequired={purchaseOrderRequired}
					POInputRef={POInputRef}
					isSubmitted={isSubmitted}
					isCouponAllowed={isCouponAllowed}
				/>
			</Media>
		</>
	);
};

export default PurchaseOrderDiscountCode;
